import type { ExpressionSpecification } from 'mapbox-gl'
import type { MapStyle, PopoverInfo } from '../../types'

/** POI LAYER */
export const POPOVER_LAYERS = [
  'sites-dots',
  'terminals-dots',
  'depots-dots',
  'sites-markers',
  'terminals-markers',
  'depots-markers',
]

export const getPolygonFillPaint = (color: string) => ({
  'fill-color': color,
  'fill-opacity': 0.15,
})

export const getPolygonOutlinePaint = (color: string) => ({
  'line-color': color,
  'line-width': 2,
  'line-opacity': 0.5,
})

export const getPoiCirclePaint = (
  color: string,
  hoveredId?: string,
  isDepot = false,
  mapStyle: MapStyle = 'default'
) => ({
  'circle-color': color,
  'circle-radius': [
    'case',
    ['==', ['get', 'id'], hoveredId || ''],
    7, // Bigger size when this feature is hovered
    [
      'interpolate',
      ['linear'],
      ['get', 'radius_miles'],
      0,
      5, // Normal size
    ],
  ] as ExpressionSpecification,
  'circle-opacity': [
    'case',
    ['==', ['get', 'id'], hoveredId || ''],
    1, // Full opacity when hovered
    isDepot || mapStyle === 'satellite' ? 0.8 : 0.4,
  ] as ExpressionSpecification,
})

export const getMarkerLayout = (key: string, hoveredId?: string) => ({
  'icon-image': `${key}-marker`, // Use dynamic icon based on key
  'icon-size': ['case', ['==', ['get', 'id'], hoveredId || ''], 0.75, 0.7] as ExpressionSpecification, // Make it bigger to see easier
  'icon-ignore-placement': true, // Prevents icon from being hidden by other layers
  'icon-offset': [0, -20] as [number, number], // Adjust vertical position
  'icon-allow-overlap': true, // Allow icons to overlap
})
/** */

/** ORDER ROUTE LAYER */
export const getLinePaint = (color: string | null) => ({
  'line-color': color || '#000', // Fallback color if not provided
  'line-width': ['get', 'lineWidth'] as ExpressionSpecification,
})

export const getArrowLayout = (arrowName: string, visibility: 'visible' | 'none') => ({
  'icon-image': arrowName, // Use a triangle icon for arrows
  'icon-allow-overlap': true,
  'symbol-placement': 'line-center' as const,
  'icon-rotate': 270,
  'icon-size': 0.6, // Adjust size of the arrow icon
  'symbol-z-order': 'viewport-y' as const,
  visibility: visibility,
})

export const getCirclePaint = (color: string | null) => ({
  'circle-color': color || '#000',
  'circle-radius': [
    'interpolate',
    ['linear'],
    ['zoom'],
    9,
    0, // At zoom 9, radius is 0
    11,
    5,
    15,
    8, // At zoom 15, radius is 8
  ] as ExpressionSpecification,
})

export const getArrivalDepartureLayout = (type: 'arrival' | 'departure', visibility: 'visible' | 'none') => ({
  'icon-image': [
    'case',
    ['==', ['get', 'action'], 'loading'],
    type === 'arrival' ? 'load-arrival-icon' : 'load-depart-icon',
    type === 'arrival' ? 'drop-arrival-icon' : 'drop-depart-icon',
  ] as ExpressionSpecification,
  'icon-size': 0.5,
  'icon-allow-overlap': true,
  visibility: visibility,
})

export const getFilter = (key: keyof Extract<PopoverInfo, { popoverType: 'arrival-departure' }>, value: string) => [
  'in',
  ['get', key] as ExpressionSpecification,
  ['literal', value] as ExpressionSpecification,
]

export const getActionPinsLayout = (visibility: 'visible' | 'none') => ({
  'icon-image': ['case', ['==', ['get', 'action'], 'loading'], 'load-marker', 'drop-marker'] as ExpressionSpecification,
  'icon-size': [
    'interpolate',
    ['linear'],
    ['zoom'],
    9,
    0.5, // At zoom 9, radius is 0.5
    11,
    0.6,
    15,
    0.7, // At zoom 15, radius is 0.7
  ] as ExpressionSpecification,
  'icon-allow-overlap': true,
  visibility: visibility,
})
/** */
